/**
 * Which calendar days an assistant has trips on.
 *
 * A booking runs from arrival to departure, so a two-night trip occupies three
 * days on the calendar, not just its start date.
 */

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Local day key ("2026-08-13"). Built from local date parts rather than
 * toISOString so a trip does not land on the wrong day for anyone whose
 * timezone is offset from UTC.
 */
export const toDayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/** Guards against a malformed departure date spinning the loop forever. */
const MAX_TRIP_DAYS = 366;

/** Every day a trip occupies, arrival through departure inclusive. */
export const eachDayKey = (start, end) => {
  const from = parseDateValue(start);
  if (!from) return [];

  const to = parseDateValue(end) || from;
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const last = new Date(to.getFullYear(), to.getMonth(), to.getDate());

  // A departure before arrival is bad data; fall back to the arrival day.
  if (last < cursor) return [toDayKey(cursor)];

  const keys = [];
  for (let i = 0; cursor <= last && i < MAX_TRIP_DAYS; i += 1) {
    keys.push(toDayKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

/**
 * Map of day key -> number of trips on that day, for a list of bookings.
 * Accepts the dashboard's normalized shape (startsAt/endsAt/scheduledAt).
 */
export const getBookedDayCounts = (bookings = []) => {
  const counts = new Map();

  (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
    const start = booking?.startsAt || booking?.scheduledAt;
    const end = booking?.endsAt || start;
    eachDayKey(start, end).forEach((key) => {
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });

  return counts;
};
